import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import { useLanguage } from '../../../hooks/useLanguage';

const Trust: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">
        {en ? 'Trust questions' : 'Vragen over vertrouwen'}
      </span>
      <h2 className="site-end__title">
        {en
          ? "Compliance, sub-processors, audit reports: ask and we'll send."
          : 'Compliance, sub-verwerkers, audit-rapporten: vraag het, we sturen het.'}
      </h2>
      <div className="site-end__action">
        <a className="btn btn-primary" href="mailto:privacy@thoughtful-oasis.com">
          privacy@thoughtful-oasis.com
        </a>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta} hidePartners>
      <Helmet>
        <title>Trust · Thoughtful Oasis</title>
        <meta
          name="description"
          content="How we handle data. Certifications, sub-processors, incident response, and the architectural premise behind audit-shaped AI."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/trust" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Trust · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="How we handle data. EU-default hosting, on-premise available, certifications, sub-processors, incident response."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/trust" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
      </Helmet>

      <header className="hero hero--sub">
        <div className="hero__inner">
          <span className="label">{en ? 'Trust' : 'Vertrouwen'}</span>
          <h1 style={{ marginTop: 20 }}>
            {en
              ? 'European by base, audit-shaped by design.'
              : 'Europees aan de basis, ontworpen om controleerbaar te zijn.'}
          </h1>
          <p className="hero__sub">
            {en
              ? 'Thoughtful Oasis BV is registered in the Netherlands. Deployments default to EU hosting; on-premise deployment is available for environments where data cannot leave the customer perimeter.'
              : 'Thoughtful Oasis BV is gevestigd in Nederland. Deployments draaien standaard op EU-hosting; on-premise deployment is beschikbaar voor omgevingen waar data de klantenperimeter niet mag verlaten.'}
          </p>
          <p className="hero__sub">
            {en
              ? 'This page is the working summary of how we handle data, where the system runs, what it is certified against, and how to reach us when something goes wrong.'
              : 'Deze pagina is de werkende samenvatting van hoe we met data omgaan, waar de AI-laag draait, waar die tegen gecertificeerd is, en hoe je ons bereikt als er iets misgaat.'}
          </p>
        </div>
      </header>

      <section className="section">
        <div className="container">
          <Reveal className="section-head prose">
            <span className="label">
              {en ? 'How we handle data' : 'Hoe we met data omgaan'}
            </span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Auditable by construction.' : 'Controleerbaar van opzet.'}
            </h2>
            <p className="body-text" style={{ marginTop: 24 }}>
              {en
                ? 'Every input that enters the system is encoded into a vector ontology whose axes are named, defined, and versioned. There is no opaque embedding step. The audit trace of every classification, retrieval or recommendation is the list of named dimensions that drove the result and the ontology version that was live at the time.'
                : 'Elke input die de AI-laag binnenkomt wordt geëncodeerd in een vector-ontologie waarvan de assen benoemd, gedefinieerd en geversioneerd zijn. Er is geen ondoorzichtige embedding-stap. Het audit-spoor van elke classificatie, retrieval of aanbeveling is de lijst van benoemde dimensies die het resultaat aandreven, plus de ontologie-versie die op dat moment live stond.'}
            </p>
            <p className="body-text" style={{ marginTop: 16 }}>
              {en
                ? 'Customer data is processed under explicit instructions, in the deployment region the customer chose, and never used to train shared encoders or shared ontologies. Pilot snapshots are scoped to the pilot agreement; production deployments run on isolated tenancy or fully on-premise.'
                : 'Klantdata wordt verwerkt onder expliciete instructies, in de deployment-regio die de klant heeft gekozen, en wordt nooit gebruikt om gedeelde encoders of gedeelde ontologieën te trainen. Pilot-snapshots vallen onder de pilot-overeenkomst; productie-deployments draaien op geïsoleerde tenancy of volledig on-premise.'}
            </p>
            <p className="body-text" style={{ marginTop: 16 }}>
              {en
                ? 'We do not run third-party analytics on this site. No tracking pixels, no session replay, no advertising integrations. Logs we keep are operational only.'
                : 'We draaien geen third-party analytics op deze site. Geen tracking-pixels, geen session replay, geen advertentie-integraties. De logs die we bijhouden zijn uitsluitend operationeel.'}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">
              {en ? 'Certifications & frameworks' : 'Certificeringen & kaders'}
            </span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'What we are certified against, and what we are working toward.'
                : 'Waar we tegen gecertificeerd zijn, en waar we naartoe werken.'}
            </h2>
          </Reveal>

          <div className="deploy-grid">
            <Reveal className="deploy-grid__cell">
              <span className="label">ISO 27001</span>
              <p>
                {en
                  ? 'In process. Information security management system documented; internal audit completed; external certification audit scheduled. Status reports available on request.'
                  : 'In behandeling. Informatiebeveiligingsmanagementsysteem (ISMS) gedocumenteerd; interne audit afgerond; externe certificeringsaudit gepland. Statusrapporten op verzoek beschikbaar.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'GDPR' : 'AVG'}</span>
              <p>
                {en
                  ? 'Conformant by construction. Named-axis embeddings make data flows auditable; lawful basis, retention, and data-subject rights are documented per deployment. The customer remains the controller; we operate as processor.'
                  : 'Conform van opzet. Embeddings met benoemde assen maken dataflows controleerbaar; rechtsgrondslag, retentie en rechten van betrokkenen worden per deployment gedocumenteerd. De klant blijft verwerkingsverantwoordelijke; wij treden op als verwerker.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'EU AI Act' : 'EU AI-verordening'}</span>
              <p>
                {en
                  ? 'Readiness is part of the architectural premise rather than a retrofit. Traceability, transparency, and human oversight are properties of the substrate. Risk classification is scoped per use case with the customer.'
                  : 'Gereedheid zit in de architectonische uitgangspunten, niet als nazorg. Herleidbaarheid, transparantie en menselijk toezicht zijn eigenschappen van de AI-laag. Risicoclassificatie wordt per use case met de klant afgebakend.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'NIS2 & sector frameworks' : 'NIS2 & sectorkaders'}
              </span>
              <p>
                {en
                  ? 'Sector-specific requirements (NIS2, BIO, sector codes) are addressed in the deployment scoping. We document which controls map to which sector framework as part of the pilot agreement.'
                  : 'Sector-specifieke eisen (NIS2, BIO, sectorcodes) worden meegenomen in de deployment-scoping. Welke controls bij welk sectorkader horen, leggen we vast als onderdeel van de pilot-overeenkomst.'}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section" id="documents">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Documents' : 'Documenten'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Current versions.' : 'Actuele versies.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'Each document is the version currently in force. Older versions are kept on file and available on request.'
                : 'Elk document is de versie die nu van kracht is. Oudere versies worden bewaard en zijn op verzoek beschikbaar.'}
            </p>
          </Reveal>

          <div className="download-list">
            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">
                  {en ? 'Privacy Policy' : 'Privacybeleid'}
                </p>
                <p className="download-list__desc">
                  {en
                    ? 'How we collect, process and retain personal data, the lawful basis we operate under, and the rights data subjects have.'
                    : 'Hoe we persoonsgegevens verzamelen, verwerken en bewaren, op welke rechtsgrondslag we werken, en welke rechten betrokkenen hebben.'}
                </p>
                <p className="download-list__meta">
                  {en ? 'Current version · EN & NL' : 'Actuele versie · EN & NL'}
                </p>
              </div>
              <Link className="paper-list__link" to="/privacy-policy">
                {en ? 'Read' : 'Lezen'}
              </Link>
            </Reveal>

            <Reveal className="download-list__item">
              <div>
                <p className="download-list__title">
                  {en ? 'Terms of Service' : 'Algemene voorwaarden'}
                </p>
                <p className="download-list__desc">
                  {en
                    ? 'The terms under which Thoughtful Oasis services are made available. Includes acceptable use, warranties, and liability framing.'
                    : 'De voorwaarden waaronder de diensten van Thoughtful Oasis beschikbaar worden gesteld. Inclusief acceptabel gebruik, garanties en aansprakelijkheidsframework.'}
                </p>
                <p className="download-list__meta">
                  {en ? 'Current version · EN & NL' : 'Actuele versie · EN & NL'}
                </p>
              </div>
              <Link className="paper-list__link" to="/tos">
                {en ? 'Read' : 'Lezen'}
              </Link>
            </Reveal>

            <Reveal as="details" id="dpa" className="disclosure">
              <summary className="disclosure__summary">
                <div>
                  <p className="disclosure__title">
                    {en ? 'Data Processing Agreement' : 'Verwerkersovereenkomst (DPA)'}
                  </p>
                  <p className="disclosure__desc">
                    {en
                      ? 'GDPR-compliant DPA for customers under which Thoughtful Oasis acts as processor. Sub-processor addendum included. The DPA is sent on request; see below to request a copy.'
                      : 'AVG-conforme verwerkersovereenkomst voor klanten waarbij Thoughtful Oasis optreedt als verwerker. Inclusief sub-verwerker-addendum. De overeenkomst wordt op verzoek toegestuurd; zie hieronder om een kopie aan te vragen.'}
                  </p>
                  <p className="disclosure__meta">
                    {en ? 'Request via form' : 'Aanvragen via formulier'}
                  </p>
                </div>
                <span className="disclosure__icon" aria-hidden="true"></span>
              </summary>
              <div className="disclosure__body">
                <p className="body-text">
                  {en
                    ? 'Request the DPA via the contact form. The link below pre-fills your message with a DPA request; add your organisation details and we will send the agreement back the same working day.'
                    : 'Vraag de verwerkersovereenkomst aan via het contactformulier. De link hieronder vult je bericht alvast in met een DPA-aanvraag; voeg je organisatiegegevens toe en we sturen de overeenkomst dezelfde werkdag terug.'}
                </p>
                <p style={{ marginTop: 24 }}>
                  <Link className="btn btn-primary" to="/contact?prefill=dpa">
                    {en
                      ? 'Request DPA via contact form'
                      : 'DPA aanvragen via contactformulier'}
                  </Link>
                </p>
              </div>
            </Reveal>

            <Reveal as="details" id="subprocessors" className="disclosure">
              <summary className="disclosure__summary">
                <div>
                  <p className="disclosure__title">
                    {en ? 'Sub-processors' : 'Sub-verwerkers'}
                  </p>
                  <p className="disclosure__desc">
                    {en
                      ? 'Short list, EU-resident where it matters. The current list of sub-processors used for hosting, observability, and operations: the role of each, the data categories they process, and their hosting region.'
                      : 'Korte lijst, EU-gevestigd waar het ertoe doet. De actuele lijst van sub-verwerkers die we inzetten voor hosting, observability en operations: hun rol, de datacategorieën die zij verwerken, en hun hosting-regio.'}
                  </p>
                  <p className="disclosure__meta">
                    {en ? 'Request via form' : 'Aanvragen via formulier'}
                  </p>
                </div>
                <span className="disclosure__icon" aria-hidden="true"></span>
              </summary>
              <div className="disclosure__body">
                <p className="body-text">
                  {en
                    ? 'Request the current sub-processor list via the contact form. The link below pre-fills your message with a sub-processor request; add your organisation details and we will send the list back the same working day.'
                    : 'Vraag de actuele sub-verwerkerslijst aan via het contactformulier. De link hieronder vult je bericht alvast in met een sub-verwerker-aanvraag; voeg je organisatiegegevens toe en we sturen de lijst dezelfde werkdag terug.'}
                </p>
                <p style={{ marginTop: 24 }}>
                  <Link className="btn btn-primary" to="/contact?prefill=subprocessors">
                    {en
                      ? 'Request sub-processor list via contact form'
                      : 'Sub-verwerkerslijst aanvragen via contactformulier'}
                  </Link>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="section-head prose">
            <span className="label">{en ? 'Incident response' : 'Incident response'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'One contact, one process, no theatre.'
                : 'Eén contact, één proces, geen show.'}
            </h2>
            <p className="body-text" style={{ marginTop: 24 }}>
              {en
                ? 'Suspected incidents (data exposure, integrity concerns, availability degradation, vulnerability reports) are handled by a single coordinator. We acknowledge inside 24 hours, assess and contain inside 72 hours, and notify affected customers in line with their contractual and regulatory obligations.'
                : 'Vermoedelijke incidenten (data-blootstelling, integriteitskwesties, beschikbaarheidsdegradatie, kwetsbaarheidsmeldingen) worden behandeld door één coördinator. We bevestigen binnen 24 uur, beoordelen en beperken binnen 72 uur, en informeren getroffen klanten in lijn met hun contractuele en regulatorische verplichtingen.'}
            </p>
            <p className="body-text" style={{ marginTop: 16 }}>
              {en
                ? 'Post-incident, every confirmed incident produces a written summary: what happened, what was affected, what was changed, and what we will do differently. Customers receive the summary as part of the closing communication.'
                : 'Na afloop levert elk bevestigd incident een schriftelijke samenvatting op: wat er gebeurd is, wat geraakt is, wat aangepast is, en wat we voortaan anders zullen doen. Klanten krijgen die samenvatting als onderdeel van de afsluitingscommunicatie.'}
            </p>
            <p style={{ marginTop: 24 }}>
              <a
                className="link-inline"
                href="mailto:privacy@thoughtful-oasis.com?subject=Incident%20report"
              >
                privacy@thoughtful-oasis.com
              </a>
              {' · '}
              {en
                ? 'monitored on business days · use the same address for vulnerability disclosure.'
                : 'op werkdagen gemonitord · gebruik hetzelfde adres voor kwetsbaarheidsmeldingen.'}
            </p>
          </Reveal>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default Trust;
